/**
 * The arithmetic core of the event-day schedule: generating a day from a shape
 * (games, break length, registration, results), and the pure operations a
 * drag-editable timeline needs (resize, move, re-anchor, pixel mapping).
 *
 * Everything here is pure — no DOM, no Angular — because this library has no
 * component-test path and these functions are the only place the feature's
 * logic can be verified at all.
 */

import { blockMinutes, cascadeScheduleStarts, ScheduleBlockValue, timeOf } from './schedule-blocks';

/**
 * Re-exported so everything that drives the timeline imports its arithmetic from
 * one module. The accessor itself lives next to DEFAULT_BLOCK_MINUTES and the
 * cascade that first established the default, which is also what keeps this
 * module's import of schedule-blocks one-directional.
 */
export { blockMinutes } from './schedule-blocks';

/** Shortest block a drag may produce. Finer is not reachable with a fingertip. */
export const MIN_BLOCK_MINUTES = 5;
/** Drag resolution. Coarser could not express a 15 minute break. */
export const SNAP_MINUTES = 5;
/** Timeline scale: a two hour block is 180px, an eight hour day 720px. */
export const PIXELS_PER_MINUTE = 1.5;
/** Shortest a rendered block may stand, so a 5 minute block still reads as a bar, not a sliver. */
export const MIN_BLOCK_PIXELS = 28;

const MINUTE_MS = 60_000;

export interface ScheduleGeneratorOptions {
    games: number;
    gameMinutes: number;
    breakMinutes: number;
    registrationMinutes: number;
    resultsMinutes: number;
    /** 09:00 (or whatever the caller passes) applied to *each* day's own date - not
     * a single instant. Callers that only have one day pass the same value for both. */
    startDate: string | Date;
    endDate?: string | Date;
    /** Hour and minute applied to every day's own date. Defaults to 09:00. */
    dayStartHour?: number;
    dayStartMinute?: number;
}

/**
 * The shape of a typical tournament day, from the design's defaults table.
 *
 * Held here rather than only in a form config because the generator falls back to
 * these when a field is blank *and* the form seeds the same numbers into its
 * inputs so an organiser can see what will be used — two readers that must not be
 * allowed to drift apart. `games` is absent on purpose: it comes from the event's
 * own numberOfRounds, which the form already knows.
 */
export const DEFAULT_GENERATOR_MINUTES: Omit<ScheduleGeneratorOptions, 'games' | 'startDate'> = {
    gameMinutes: 120,
    breakMinutes: 15,
    registrationMinutes: 30,
    resultsMinutes: 15
};

const toDate = (value: string | Date | undefined | null): Date | null => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

/** Inclusive day span between two dates. A missing or invalid endDate, or one
 * before startDate, is a one-day event - never negative, never zero. */
export function dayCount(startDate: string | Date | undefined, endDate: string | Date | undefined): number {
    const start = toDate(startDate);
    const end = toDate(endDate);
    if (!start || !end || end < start) {
        return 1;
    }
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000)) + 1;
}

/**
 * A whole tournament day repeated for every day of the event: registration,
 * games separated by breaks, then results — with registration on every day but
 * results only on the last, and round numbers continuing across the boundary
 * rather than restarting each day.
 *
 * Games are labelled "Round N" because round-reminder.ts derives a round number
 * from the digits in the label — relabelling them silently breaks push reminders.
 * A zero-length section is omitted entirely, so "three games back to back" is
 * expressible without inventing empty blocks.
 */
export function generateSchedule(options: ScheduleGeneratorOptions): ScheduleBlockValue[] {
    const days = dayCount(options.startDate, options.endDate);
    const games = Math.max(Math.floor(options.games) || 0, 0);
    const hour = options.dayStartHour ?? 9;
    const minute = options.dayStartMinute ?? 0;

    const start = toDate(options.startDate) ?? new Date();
    let roundCounter = 0;
    const allBlocks: ScheduleBlockValue[] = [];

    for (let day = 0; day < days; day++) {
        const dayBlocks: ScheduleBlockValue[] = [];
        const push = (label: string, type: string, durationMinutes: number) => {
            if (durationMinutes > 0) {
                dayBlocks.push({ label, type, durationMinutes, startTime: undefined });
            }
        };

        // Registration appears every day; results only on the last - the two
        // deliberate exceptions to "every day is identical" (see the revisions doc).
        push('Registration', 'registration', options.registrationMinutes);

        // Whole games only: a fractional count would otherwise leave a trailing break
        // after what was meant to be the last game.
        if (options.gameMinutes > 0) {
            for (let game = 1; game <= games; game++) {
                roundCounter++;
                push(`Round ${roundCounter}`, 'round', options.gameMinutes);
                // Breaks separate games; there is none after the last.
                if (game < games) {
                    push('Break', 'break', options.breakMinutes);
                }
            }
        }

        if (day === days - 1) {
            push('Results & Prizes', 'results', options.resultsMinutes);
        }

        const dayDate = new Date(start.getFullYear(), start.getMonth(), start.getDate() + day, hour, minute, 0, 0);
        // shiftScheduleTo anchors day N's own first block and cascades within it -
        // it never looks at any other day's blocks, which is what keeps day two
        // from chaining off day one's last block instead of starting fresh at 09:00.
        allBlocks.push(...shiftScheduleTo(dayBlocks, dayDate));
    }

    return allBlocks;
}

export interface ScheduleDayGroup {
    /** Midnight of this day, for a date heading and for the empty-day case. */
    date: Date;
    blocks: ScheduleBlockValue[];
}

/**
 * Groups blocks by calendar day, from the event's own date range rather than
 * inferring the day count from the blocks - an empty middle day in a three-day
 * event (nobody has added blocks to it yet, or every block was deleted) still
 * renders as an empty day instead of vanishing entirely.
 */
export function groupBlocksByDay(
    blocks: ScheduleBlockValue[],
    startDate: string | Date | undefined,
    endDate: string | Date | undefined
): ScheduleDayGroup[] {
    const start = toDate(startDate);
    const days = dayCount(startDate, endDate);
    if (!start) {
        return [{ date: new Date(), blocks: blocks ?? [] }];
    }

    const groups: ScheduleDayGroup[] = [];
    for (let day = 0; day < days; day++) {
        groups.push({ date: new Date(start.getFullYear(), start.getMonth(), start.getDate() + day), blocks: [] });
    }

    for (const block of blocks ?? []) {
        const blockDate = toDate(block.startTime);
        if (!blockDate) {
            continue;
        }
        const dayIndex = Math.round(
            (new Date(blockDate.getFullYear(), blockDate.getMonth(), blockDate.getDate()).getTime() -
                new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime()) /
                (24 * 60 * 60 * 1000)
        );
        if (groups[dayIndex]) {
            groups[dayIndex].blocks.push(block);
        }
        // A block outside every group's date is exactly the stranded-block case -
        // Task 8 (6e) decides what the component does about it. This function's
        // only job is grouping; dropping it here would make that decision silently
        // rather than somewhere the spec's "never silently delete" requirement can
        // actually be honoured.
    }

    return groups;
}

/**
 * Change one block's length and re-time everything after it. Clamped to a minimum
 * because dragging an edge past the top of its own block is easy and would
 * otherwise produce a zero or negative duration.
 */
export function resizeBlock(blocks: ScheduleBlockValue[], index: number, minutes: number): ScheduleBlockValue[] {
    if (!blocks?.[index]) {
        return blocks ?? [];
    }

    const resized = [...blocks];
    resized[index] = {
        ...resized[index],
        durationMinutes: Math.max(Number.isFinite(minutes) ? minutes : MIN_BLOCK_MINUTES, MIN_BLOCK_MINUTES)
    };

    return cascadeScheduleStarts(resized, index);
}

/** Move a block to a new position, then re-time the day from its first block. */
export function moveBlock(blocks: ScheduleBlockValue[], from: number, to: number): ScheduleBlockValue[] {
    const list = blocks ?? [];
    if (from === to || !list[from] || to < 0 || to >= list.length) {
        return list;
    }

    const reordered = [...list];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    return shiftScheduleTo(reordered, list[0]?.startTime);
}

/**
 * Re-anchor the schedule so the first block starts at the given time, with every
 * later block following in turn. Used both to move a day and to lay out freshly
 * generated blocks that carry durations but no times yet.
 */
export function shiftScheduleTo(blocks: ScheduleBlockValue[], startTime: string | Date | undefined): ScheduleBlockValue[] {
    const list = blocks ?? [];
    const start = toDate(startTime);
    if (!list.length || !start) {
        return list;
    }

    const anchored = [...list];
    anchored[0] = { ...anchored[0], startTime: start.toISOString() };

    return cascadeScheduleStarts(anchored, 0);
}

/** When the last block finishes. */
export function scheduleEnd(blocks: ScheduleBlockValue[]): Date | null {
    const last = (blocks ?? [])[(blocks ?? []).length - 1];
    const start = toDate(last?.startTime);
    if (!start) {
        return null;
    }
    return new Date(start.getTime() + blockMinutes(last) * MINUTE_MS);
}

export function minutesToPixels(minutes: number, pixelsPerMinute: number = PIXELS_PER_MINUTE): number {
    return Math.max(minutes * pixelsPerMinute, MIN_BLOCK_PIXELS);
}

/**
 * Pixel distance a drag must travel past a neighbouring block before a reorder
 * swap registers against it — half that neighbour's own rendered height, so the
 * dragged block visibly passes the neighbour's midpoint before they trade places.
 * Uses minutesToPixels (the same floor a block's own height renders with), not
 * raw duration, so a swap past a very short block still needs a sensible amount
 * of travel rather than the ~4px half of its true, unrendered 7.5px.
 */
export function swapThresholdPx(block: ScheduleBlockValue, pixelsPerMinute: number = PIXELS_PER_MINUTE): number {
    return minutesToPixels(blockMinutes(block), pixelsPerMinute) / 2;
}

/** Drag distance back to minutes, snapped so a fingertip lands on a usable value. */
export function pixelsToMinutes(pixels: number, pixelsPerMinute: number = PIXELS_PER_MINUTE, snapMinutes: number = SNAP_MINUTES): number {
    const minutes = pixels / pixelsPerMinute;
    return Math.round(minutes / snapMinutes) * snapMinutes;
}

export interface TimeAxisTick {
    label: string;
    offsetPx: number;
}

/** Hour marks for a time axis, positioned against the same scale the blocks render at. */
export function timeAxisTicks(blocks: ScheduleBlockValue[], pixelsPerMinute: number = PIXELS_PER_MINUTE): TimeAxisTick[] {
    const start = toDate(blocks?.[0]?.startTime);
    const end = scheduleEnd(blocks);
    if (!start || !end) {
        return [];
    }

    const ticks: TimeAxisTick[] = [];
    const first = new Date(start);
    first.setMinutes(0, 0, 0);
    if (first < start) {
        first.setHours(first.getHours() + 1);
    }

    for (let hour = new Date(first); hour <= end; hour.setHours(hour.getHours() + 1)) {
        const offsetMinutes = (hour.getTime() - start.getTime()) / 60000;
        // Not minutesToPixels: its MIN_BLOCK_PIXELS floor exists so a very short *block*
        // still renders as a visible bar. A tick is a point in time, not a rendered bar,
        // so that floor would push a tick landing at minute 0 (a schedule starting exactly
        // on the hour) down to 28px instead of 0 — raw multiplication is what's wanted here.
        ticks.push({ label: timeOf(hour), offsetPx: offsetMinutes * pixelsPerMinute });
    }

    return ticks;
}
